import React from 'react';

import { EditOutlined } from '@ant-design/icons';
import UserOutlined from '@ant-design/icons/lib/icons/UserOutlined';
import styled from 'styled-components';

import Avatar from 'common/Avatar';
import { ActionPlanDetailsType } from 'types/actionPlans';

import { getActionPlanActorName, getActionPlanSourceLabel, isEditedEntity } from './actionPlanMetaUtils';
import FocusAreaTag from './FocusAreaTag';
import useActionPlanFocusAreas from './useActionPlanFocusAreas';
import { ACTOR_TYPE } from '../../constants';

const TitleCardWrapper = styled.div`
  border-radius: 10px;
  border: 1px solid var(--line-gray);
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  overflow: hidden;
`;

const TitleMain = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  padding: 30px;
`;

const BackIcon = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid var(--line-gray);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
  cursor: pointer;
`;

const Title = styled.div`
  font-size: 20px;
  font-family: var(--font-bold);
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const TitleContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
  min-width: 0;
`;

const SectionDivider = styled.div`
  width: 1px;
  flex-shrink: 0;
  align-self: stretch;
  background-image: linear-gradient(
    to bottom,
    var(--background-gray-light) 0%,
    var(--line-gray) 50%,
    var(--background-gray-light)
  );
`;
const OwnerSummary = styled.div`
  width: 260px;
  flex-shrink: 0;
  padding: 20px 20px 20px 15px;
`;

const OwnerHeading = styled.div`
  font-size: 10px;
  align-self: stretch;
  letter-spacing: 2px;
  color: var(--text-secondary);
  margin-bottom: 5px;
`;

const OwnerAssignedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const OwnerAvatar = styled(Avatar)`
  margin-right: 10px;
`;

const OwnerInfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  text-align: left;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const OwnerPosition = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const CreatedAt = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const SourceText = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const ModifiedText = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
  font-family: var(--font-bold);
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

const WhyPlanTitle = styled.div`
  margin-top: 20px;
  font-size: 16px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const DescriptionText = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 19px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

const DescriptionLine = styled.div`
  color: var(--text-secondary);
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const DescriptionLabel = styled.span`
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

type Props = {
  actionPlan?: ActionPlanDetailsType;
  createdAtLabel: string;
  fallbackCreatedByName?: string;
  onBack: () => void;
};

const ActionPlanDetailsTitleCard = ({ actionPlan, createdAtLabel, fallbackCreatedByName, onBack }: Props) => {
  const { focusAreaLabelById } = useActionPlanFocusAreas();
  const aiGenerated = actionPlan?.is_ai_generated !== false;
  const createdByName = getActionPlanActorName(actionPlan?.created_by);
  const sourceLabel = getActionPlanSourceLabel(
    aiGenerated,
    createdByName,
    fallbackCreatedByName,
    actionPlan?.source,
    actionPlan?.source_insights,
    actionPlan?.related_alert_type_id
  );
  const isEdited = isEditedEntity(actionPlan ?? {});
  const descriptionLines = (actionPlan?.description || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <TitleCardWrapper data-test-id="action-plan-details-title-card">
      <TitleMain data-test-id="action-plan-details-title-main">
        <BackIcon data-test-id="action-plan-details-back-button" onClick={onBack}>
          <img src="/icons/chevron-left-black.svg" alt="chevron-lef" height="24px" width="24px" />
        </BackIcon>
        <TitleContent data-test-id="action-plan-details-title-content">
          <Title data-test-id="action-plan-details-title">{actionPlan?.name || ''}</Title>
          <MetaRow data-test-id="action-plan-details-meta-row">
            {actionPlan?.focus_area_id != null && focusAreaLabelById[actionPlan.focus_area_id] ? (
              <FocusAreaTag label={focusAreaLabelById[actionPlan.focus_area_id]} />
            ) : null}
            <SourceText>{sourceLabel}</SourceText>
            {isEdited && (
              <ModifiedText>
                <EditOutlined />
                Edited
              </ModifiedText>
            )}
            {createdAtLabel && <CreatedAt data-test-id="action-plan-details-created-at">{createdAtLabel}</CreatedAt>}
          </MetaRow>
          <WhyPlanTitle data-test-id="action-plan-details-why-title">Why This Plan Exists</WhyPlanTitle>
          <DescriptionText data-test-id="action-plan-details-description">
            {descriptionLines.map((line, index) => {
              const separatorIndex = line.indexOf(':');
              if (separatorIndex === -1) {
                return <DescriptionLine key={`${line}-${index}`}>{line}</DescriptionLine>;
              }

              const label = line.slice(0, separatorIndex).trim();
              const value = line.slice(separatorIndex + 1).trim();

              return (
                <DescriptionLine key={`${label}-${index}`}>
                  <DescriptionLabel>{label}:</DescriptionLabel>
                  {value ? ` ${value}` : ''}
                </DescriptionLine>
              );
            })}
          </DescriptionText>
        </TitleContent>
      </TitleMain>
      <SectionDivider aria-hidden="true" />
      <OwnerSummary data-test-id="action-plan-details-owner-summary">
        <OwnerHeading>PLAN OWNER</OwnerHeading>
        {actionPlan?.owner ? (
          <OwnerAssignedRow data-test-id="action-plan-details-owner-assigned-row">
            <OwnerAvatar
              size={30}
              type={ACTOR_TYPE.USER}
              name={actionPlan.owner.name}
              src={actionPlan.owner.avatar_url || undefined}
              color="var(--negative-orange)"
            />
            <OwnerInfoBlock data-test-id="action-plan-details-owner-info-block">
              <span data-test-id="action-plan-details-owner-name">{actionPlan.owner.name}</span>
              {actionPlan.owner.position_rights ? (
                <OwnerPosition data-test-id="action-plan-details-owner-position">
                  {actionPlan.owner.position_rights}
                </OwnerPosition>
              ) : null}
            </OwnerInfoBlock>
          </OwnerAssignedRow>
        ) : (
          <OwnerAssignedRow data-test-id="action-plan-details-owner-unassigned-row">
            <OwnerAvatar size={30} type={ACTOR_TYPE.USER} icon={<UserOutlined />} />
            <span data-test-id="action-plan-details-owner-unassigned-label">Unassigned</span>
          </OwnerAssignedRow>
        )}
      </OwnerSummary>
    </TitleCardWrapper>
  );
};

export default ActionPlanDetailsTitleCard;
